import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CheckCircle, Envelope, Info, Lock, Eye, EyeSlash, ArrowRight, Warning } from '@phosphor-icons/react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { AGB_VERSION } from './legal/Terms';

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const ActivateAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth, language } = useAuth();
  const token = searchParams.get('token') || '';
  const L = (en, de) => (language === 'de' ? de : en);

  const [phase, setPhase] = useState(token ? 'verifying' : 'email'); // verifying | email | emailSent | form | invalid | expired | already | notfound
  const [candidate, setCandidate] = useState(null);
  const [error, setError] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [claimToken, setClaimToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const activeToken = token || claimToken;

  useEffect(() => {
    let active = true;
    const verify = async () => {
      // No token in URL → show email lookup form.
      if (!token) {
        setPhase('email');
        return;
      }
      try {
        const { data } = await API.post('/auth/activate/verify', { token });
        if (!active) return;
        if (data.already_activated) {
          setPhase('already');
          setCandidate(data);
        } else {
          setCandidate(data);
          setPhase('form');
        }
      } catch (err) {
        if (!active) return;
        const status = err.response?.status;
        if (status === 410) setPhase('expired');
        else setPhase('invalid');
        setError(err.response?.data?.detail || '');
      }
    };
    verify();
    return () => { active = false; };
  }, [token]);

  const handleClaim = async (e) => {
    e.preventDefault();
    setError('');
    const email = emailInput.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setError(L('Please enter a valid email address.', 'Bitte gib eine gültige E-Mail-Adresse ein.'));
      return;
    }
    setSubmitting(true);
    try {
      await API.post('/auth/claim', { email, origin_url: window.location.origin });
      setPhase('emailSent');
    } catch (err) {
      if (err.response?.status === 429) {
        setError(L('Too many attempts. Please try again in 15 minutes.', 'Zu viele Versuche. Bitte in 15 Minuten erneut versuchen.'));
      } else {
        setError(err.response?.data?.detail || L('Lookup failed. Please try again.', 'Suche fehlgeschlagen. Bitte erneut versuchen.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError(L('Password must be at least 6 characters.', 'Passwort muss mindestens 6 Zeichen haben.'));
      return;
    }
    if (password !== confirm) {
      setError(L('Passwords do not match.', 'Passwörter stimmen nicht überein.'));
      return;
    }
    if (!agbAccepted) {
      setError(L('Please accept the Terms and Privacy Policy.', 'Bitte akzeptiere die AGB und Datenschutzerklärung.'));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await API.post('/auth/activate/complete', { token: activeToken, password, agb_version: AGB_VERSION });
      if (data.access_token) {
        localStorage.setItem('pa_access_token', data.access_token);
        if (data.refresh_token) localStorage.setItem('pa_refresh_token', data.refresh_token);
      }
      await checkAuth();
      toast.success(L('Account activated! Welcome back.', 'Konto aktiviert! Willkommen zurück.'));
      setTimeout(() => navigate('/onboarding/level-quiz'), 800);
    } catch (err) {
      setError(err.response?.data?.detail || L('Activation failed. Please try again.', 'Aktivierung fehlgeschlagen. Bitte erneut versuchen.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-slate-100 flex items-center justify-center px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-emerald-700 font-black text-2xl tracking-tight" data-testid="brand-title">
            <span className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-lg shadow-emerald-500/30">P</span>
            Padelarena Wädenswil
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 sm:p-8" data-testid="activate-card">
          {phase === 'verifying' && (
            <div className="py-8 text-center text-slate-500 animate-pulse" data-testid="activate-loading">
              {L('Checking your link…', 'Link wird geprüft…')}
            </div>
          )}

          {phase === 'email' && (
            <div className="space-y-5" data-testid="activate-email-lookup">
              <div className="text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3">
                  <Envelope size={26} weight="bold" />
                </div>
                <h1 className="text-2xl font-black text-slate-900">
                  {L('Activate your account', 'Konto aktivieren')}
                </h1>
                <p className="text-sm text-slate-600 mt-2">
                  {L(
                    'Enter the email address you used on Playtomic. If we find your account, you can set a password and start booking right away.',
                    'Gib die E-Mail-Adresse ein, die du bei Playtomic verwendet hast. Wenn wir dein Konto finden, kannst du direkt ein Passwort setzen und loslegen.'
                  )}
                </p>
              </div>

              <form onSubmit={handleClaim} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-slate-700 font-semibold">
                    {L('Email address', 'E-Mail-Adresse')}
                  </Label>
                  <div className="relative mt-1">
                    <Envelope size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      id="email"
                      type="text"
                      inputMode="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      autoComplete="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      className="pl-10"
                      placeholder="name@example.ch"
                      autoFocus
                      required
                      data-testid="claim-email-input"
                    />
                  </div>
                </div>

                {error && (
                  <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm" data-testid="claim-error">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                  data-testid="claim-submit-btn"
                >
                  {submitting ? L('Searching…', 'Suche…') : L('Continue', 'Weiter')}
                  {!submitting && <ArrowRight size={16} className="ml-1" />}
                </Button>
              </form>

              <div className="text-center text-xs text-slate-500 space-y-1 pt-2 border-t border-slate-100">
                <p>
                  {L('No Playtomic account? ', 'Kein Playtomic-Konto? ')}
                  <Link to="/register" className="text-emerald-700 font-semibold underline">
                    {L('Create a new account', 'Neues Konto erstellen')}
                  </Link>
                </p>
                <p>
                  {L('Problems? Email ', 'Probleme? Schreib an ')}
                  <a href="mailto:info@padelarena.ch" className="text-emerald-700 font-semibold underline">info@padelarena.ch</a>
                </p>
              </div>
            </div>
          )}

          {phase === 'emailSent' && (
            <div className="text-center space-y-4" data-testid="activate-email-sent">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <Envelope size={26} weight="bold" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">
                {L('Check your inbox', 'Prüfe deinen Posteingang')}
              </h1>
              <p className="text-sm text-slate-600">
                {L(
                  'If an account with this email exists, we just sent an activation link. The link is valid for 24 hours. Please also check your spam folder.',
                  'Falls ein Konto mit dieser E-Mail existiert, haben wir dir gerade einen Aktivierungs-Link geschickt. Der Link ist 24 Stunden gültig. Bitte prüfe auch deinen Spam-Ordner.'
                )}
              </p>
              <div className="text-xs text-slate-500 pt-2">
                {L('Problems? Email ', 'Probleme? Schreib an ')}
                <a href="mailto:info@padelarena.ch" className="text-emerald-700 font-semibold underline">info@padelarena.ch</a>
              </div>
              <Button
                variant="outline"
                onClick={() => { setPhase('email'); setError(''); setEmailInput(''); }}
                data-testid="email-sent-try-again-btn"
              >
                {L('Use a different email', 'Andere E-Mail verwenden')}
              </Button>
            </div>
          )}

          {phase === 'notfound' && (
            <div className="text-center space-y-4" data-testid="activate-notfound">
              <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center">
                <Warning size={26} weight="bold" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">{L('Email not found', 'E-Mail nicht gefunden')}</h1>
              <p className="text-sm text-slate-600">
                {L(
                  'We could not find a Playtomic account with this email address. If you played at Padelarena Wädenswil before, please contact ',
                  'Wir konnten kein Playtomic-Konto mit dieser E-Mail finden. Falls du bereits bei Padelarena Wädenswil gespielt hast, kontaktiere bitte '
                )}
                <a href="mailto:info@padelarena.ch" className="text-emerald-700 font-semibold underline">info@padelarena.ch</a>.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setPhase('email'); setError(''); }}
                  data-testid="notfound-try-again-btn"
                >
                  {L('Try another email', 'Andere E-Mail versuchen')}
                </Button>
                <Link to="/register">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto" data-testid="notfound-register-btn">
                    {L('Create new account', 'Neues Konto erstellen')}
                    <ArrowRight size={16} className="ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {phase === 'invalid' && (
            <div className="text-center space-y-4" data-testid="activate-invalid">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                <Warning size={26} weight="bold" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">{L('Invalid link', 'Ungültiger Link')}</h1>
              <p className="text-sm text-slate-600">
                {L('This activation link is invalid. If you believe this is an error, please email ', 'Dieser Aktivierungs-Link ist ungültig. Falls du glaubst, dass dies ein Fehler ist, schreib bitte an ')}
                <a href="mailto:info@padelarena.ch" className="text-emerald-700 font-semibold underline">info@padelarena.ch</a>.
              </p>
              <Link to="/register">
                <Button variant="outline" className="mt-2" data-testid="activate-invalid-register-btn">
                  {L('Create new account', 'Neues Konto erstellen')}
                </Button>
              </Link>
            </div>
          )}

          {phase === 'expired' && (
            <div className="text-center space-y-4" data-testid="activate-expired">
              <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center">
                <Warning size={26} weight="bold" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">{L('Link expired', 'Link abgelaufen')}</h1>
              <p className="text-sm text-slate-600">
                {L('This activation link has expired. Please contact us at ', 'Dieser Aktivierungs-Link ist abgelaufen. Bitte kontaktiere uns unter ')}
                <a href="mailto:info@padelarena.ch" className="text-emerald-700 font-semibold underline">info@padelarena.ch</a>
                {L(' for a new one.', ' für einen neuen Link.')}
              </p>
            </div>
          )}

          {phase === 'already' && (
            <div className="text-center space-y-4" data-testid="activate-already">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <CheckCircle size={26} weight="bold" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">{L('Already activated', 'Bereits aktiviert')}</h1>
              <p className="text-sm text-slate-600">
                {L('This account has already been activated. Please log in with your password.', 'Dieses Konto wurde bereits aktiviert. Bitte melde dich mit deinem Passwort an.')}
              </p>
              <div className="flex gap-2 justify-center">
                <Link to="/login">
                  <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="activate-already-login-btn">
                    {L('Go to login', 'Zum Login')} <ArrowRight size={16} className="ml-1" />
                  </Button>
                </Link>
                <Link to="/forgot-password">
                  <Button variant="outline" data-testid="activate-already-forgot-btn">
                    {L('Forgot password?', 'Passwort vergessen?')}
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {phase === 'form' && candidate && (
            <>
              <div className="text-center mb-5">
                <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3">
                  <CheckCircle size={26} weight="bold" />
                </div>
                <h1 className="text-2xl font-black text-slate-900" data-testid="activate-greeting">
                  {L('Welcome back', 'Willkommen zurück')}{candidate.name ? `, ${candidate.name.split(' ')[0]}` : ''}!
                </h1>
                <p className="text-sm text-slate-600 mt-1">
                  {L('Set a password to activate your new account.', 'Wähle ein Passwort, um dein neues Konto zu aktivieren.')}
                </p>
              </div>

              {/* Info banner: email is already set from Playtomic */}
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-4 mb-5" data-testid="activate-email-info">
                <div className="flex gap-3">
                  <Info size={22} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-700 leading-relaxed">
                    <p className="mb-1">
                      {L('Your account is already set up with the same email address you used on Playtomic:', 'Dein Konto wurde bereits mit der gleichen E-Mail-Adresse eingerichtet, die du auf Playtomic verwendet hast:')}
                    </p>
                    <p className="inline-flex items-center gap-1.5 bg-white border border-emerald-200 rounded-md px-2 py-1 font-mono text-emerald-800 text-xs" data-testid="activate-email">
                      <Envelope size={14} /> {candidate.email}
                    </p>
                    <p className="mt-3 text-xs text-slate-600">
                      {L('Prefer to use a different email? ', 'Möchtest du eine andere E-Mail-Adresse nutzen? ')}
                      <Link to="/register" className="text-emerald-700 font-semibold underline" data-testid="activate-goto-register">
                        {L('Create a new account instead', 'Neues Konto erstellen')}
                      </Link>.
                    </p>
                    <p className="mt-1 text-xs text-slate-600">
                      {L('Any problems? Email ', 'Probleme? Schreib an ')}
                      <a href="mailto:info@padelarena.ch" className="text-emerald-700 font-semibold underline">info@padelarena.ch</a>.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="activate-form">
                <div>
                  <Label htmlFor="password" className="text-slate-700 font-semibold">
                    {L('New password', 'Neues Passwort')}
                  </Label>
                  <div className="relative mt-1">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      placeholder={L('At least 6 characters', 'Mindestens 6 Zeichen')}
                      autoFocus
                      required
                      data-testid="activate-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      tabIndex={-1}
                      data-testid="activate-password-toggle"
                    >
                      {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm" className="text-slate-700 font-semibold">
                    {L('Confirm password', 'Passwort bestätigen')}
                  </Label>
                  <div className="relative mt-1">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      id="confirm"
                      type={showPassword ? 'text' : 'password'}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="activate-confirm-input"
                    />
                  </div>
                </div>

                {error && (
                  <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm" data-testid="activate-error">
                    {error}
                  </div>
                )}

                <div className="flex items-start gap-3 pt-1" data-testid="activate-agb-wrap">
                  <Checkbox
                    id="activate-agb"
                    checked={agbAccepted}
                    onCheckedChange={(v) => setAgbAccepted(!!v)}
                    className="mt-0.5"
                    data-testid="activate-agb-checkbox"
                  />
                  <label htmlFor="activate-agb" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                    {L(
                      <>
                        I accept the{' '}
                        <Link to="/terms" target="_blank" className="text-emerald-700 underline font-semibold">Terms</Link>
                        {' '}and{' '}
                        <Link to="/privacy" target="_blank" className="text-emerald-700 underline font-semibold">Privacy Policy</Link>.
                        {' '}Cancellation: <strong>courts 24h</strong>, <strong>mixins/events 2h</strong> before start (free).
                      </>,
                      <>
                        Ich akzeptiere die{' '}
                        <Link to="/agb" target="_blank" className="text-emerald-700 underline font-semibold">AGB</Link>
                        {' '}und die{' '}
                        <Link to="/privacy" target="_blank" className="text-emerald-700 underline font-semibold">Datenschutzerklärung</Link>.
                        {' '}Stornierung: <strong>Courts 24h</strong>, <strong>Mixins/Events 2h</strong> vor Beginn (kostenlos).
                      </>
                    )}
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={submitting || !agbAccepted}
                  className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                  data-testid="activate-submit-btn"
                >
                  {submitting
                    ? L('Activating…', 'Wird aktiviert…')
                    : L('Activate account', 'Konto aktivieren')}
                </Button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-xs text-slate-500 mt-4">
          © {new Date().getFullYear()} Padelarena Wädenswil ·{' '}
          <a href="mailto:info@padelarena.ch" className="underline hover:text-emerald-700">info@padelarena.ch</a>
        </p>
      </motion.div>
    </div>
  );
};

export default ActivateAccount;
